import { Plus, Minus, ShoppingBag, X, Truck, ShieldCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCart } from "@/contexts/CartContext";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { useCurrency } from "@/contexts/CurrencyContext";

const CartSidebar = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, totalPrice, totalItems } =
    useCart();
  const navigate = useNavigate();
  const { data: storeSettings } = useStoreSettings();
  const { formatPrice } = useCurrency();

  const shippingThreshold = storeSettings?.free_shipping_threshold ?? 999;
  const shippingCost = storeSettings?.default_shipping_rate ?? 99;

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate("/checkout");
  };

  const handleContinue = () => setIsCartOpen(false);

  const shippingAmount = totalPrice >= shippingThreshold ? 0 : shippingCost;
  const totalWithShipping = totalPrice + shippingAmount;
  const progress = Math.min(100, Math.round((totalPrice / shippingThreshold) * 100));
  const remainingForFreeShipping = Math.max(0, shippingThreshold - totalPrice);

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent
        side="right"
        className="flex flex-col w-full sm:max-w-md p-0 bg-cream border-l border-border/60"
      >
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-border/60 bg-cream/95 backdrop-blur-sm">
          <SheetTitle className="flex items-center justify-between font-display text-lg text-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-terracotta/10 flex items-center justify-center">
                <ShoppingBag className="h-4 w-4 text-terracotta" />
              </span>
              Your Cart
              {totalItems > 0 && (
                <span className="text-xs font-medium text-muted-foreground">
                  · {totalItems} {totalItems === 1 ? "item" : "items"}
                </span>
              )}
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center">
            <div className="relative">
              <div className="absolute inset-0 bg-terracotta/15 blur-2xl rounded-full" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-cream to-secondary flex items-center justify-center border border-border/50 shadow-soft">
                <ShoppingBag className="h-10 w-10 text-terracotta/70" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="font-display text-xl text-foreground">Your basket is empty</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                Discover handcrafted pieces from village artisans — each one carries a story.
              </p>
            </div>
            <Button
              variant="accent"
              onClick={handleContinue}
              className="mt-2 group"
            >
              Explore the craft
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        ) : (
          <>
            {/* Free shipping progress */}
            <div className="px-5 pt-4 pb-3 bg-cream border-b border-border/40">
              {shippingAmount === 0 ? (
                <div className="flex items-center gap-2 text-sm text-olive">
                  <Truck className="h-4 w-4" />
                  <span>You've unlocked free shipping.</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Truck className="h-3.5 w-3.5 text-terracotta" />
                      Add{" "}
                      <strong className="text-foreground">
                        {formatPrice(remainingForFreeShipping)}
                      </strong>{" "}
                      for free shipping
                    </span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-secondary/70 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-terracotta to-golden transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Items */}
            <ScrollArea className="flex-1">
              <ul className="px-5 py-4 space-y-3">
                {items.map((item) => {
                  const lineTotal = item.price * item.quantity;
                  return (
                    <li
                      key={item.id}
                      className="group relative flex gap-3.5 p-3 rounded-2xl bg-background border border-border/60 hover:border-terracotta/40 hover:shadow-soft transition-all"
                    >
                      <div className="w-[78px] h-[78px] rounded-xl overflow-hidden bg-secondary flex-shrink-0 ring-1 ring-border/50">
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm text-foreground line-clamp-2 leading-snug pr-1">
                            {item.name}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            aria-label={`Remove ${item.name}`}
                            className="text-muted-foreground/60 hover:text-destructive transition-colors p-0.5 -mr-1 -mt-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
                          {formatPrice(item.price)} each
                        </p>

                        <div className="mt-auto pt-2 flex items-center justify-between">
                          <div className="inline-flex items-center bg-secondary/70 rounded-full p-0.5 border border-border/50">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              aria-label="Decrease quantity"
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-cream text-foreground transition-colors"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-7 text-center text-sm font-semibold text-foreground tabular-nums">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              aria-label="Increase quantity"
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-cream text-foreground transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>

                          <span className="font-semibold text-sm text-terracotta tabular-nums">
                            {formatPrice(lineTotal)}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Reassurance strip */}
              <div className="mx-5 mb-4 px-3 py-2.5 rounded-xl bg-secondary/50 border border-border/40 flex items-center gap-2 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-olive flex-shrink-0" />
                <span>
                  Secure checkout · Fair wages reach the artisan directly.
                </span>
              </div>
            </ScrollArea>

            {/* Summary footer */}
            <div className="border-t border-border/60 bg-cream/95 backdrop-blur-sm p-5 space-y-4">
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="tabular-nums text-foreground">{formatPrice(totalPrice)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span className={`tabular-nums ${shippingAmount === 0 ? "text-olive font-medium" : "text-foreground"}`}>
                    {shippingAmount === 0 ? "Free" : formatPrice(shippingAmount)}
                  </span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between items-baseline">
                  <span className="font-display text-base text-foreground">Total</span>
                  <span className="font-display text-xl text-terracotta tabular-nums">
                    {formatPrice(totalWithShipping)}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground/80 pt-0.5">
                  Taxes calculated at checkout.
                </p>
              </div>

              <Button
                className="w-full bg-terracotta hover:bg-terracotta/90 text-cream group"
                size="lg"
                onClick={handleCheckout}
              >
                Proceed to Checkout
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
              <button
                type="button"
                onClick={handleContinue}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Continue shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
